/**
 * Audit Entity - RTK Query API
 * API endpoints for Audit domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { archiveArgs } from '../../../shared/api/archiveArgs';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

// AUD-26 (D7): evidence files are streamed by the tenant-scoped download
// action -- never a storage URL. Fetched as a blob through the authenticated
// base query, then saved via a temporary object URL (same as ethics).
const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

// AUD-26: evidence is multipart (with an optional file). Content-Type is
// left to the browser so it sets the boundary.
const buildEvidenceFormData = (data) => {
  const form = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    form.append(key, value);
  });
  return form;
};

const listParams = (params) => {
  const out = { page_size: 100 };
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      out[key] = value;
    }
  });
  return out;
};

export const auditApiTestUtils = { buildEvidenceFormData, filenameFromDisposition };

export const auditApi = createApi({
  reducerPath: 'auditApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'AuditPlan',
    'Engagement',
    'EngagementMilestone',
    'EngagementDependency',
    'Workpaper',
    'Finding',
    'Issue',
    'AuditStats',
    'WorkflowTransition',
    // AUD-14/16/26
    'EngagementTeam',
    'Procedure',
    'Evidence',
    'AuditTemplate',
    'AuditUniverse',
  ],
  endpoints: (builder) => ({
    // =========================================================================
    // Audit Plans
    // =========================================================================
    getAuditPlans: builder.query({
      query: ({ page = 1, limit = 20, year, filters = {} } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());
        if (year) {
          params.set('year', year);
        }
        if (filters.status && filters.status !== 'all') {
          params.set('status', filters.status);
        }
        if (filters.type && filters.type !== 'all') {
          // AUD-07: AuditPlanViewSet.filterset_fields is
          // ['status', 'plan_type', 'year'] -- there is no 'type' filter,
          // so this param was silently dropped by DjangoFilterBackend and
          // the Type dropdown never actually filtered anything.
          params.set('plan_type', filters.type);
        }
        if (filters.search) {
          params.set('search', filters.search);
        }

        return {
          url: `/api/v1/audits/plans/?${params.toString()}`,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: ['AuditPlan'],
    }),

    getAuditPlanById: builder.query({
      query: (id) => ({
        url: `/api/v1/audits/plans/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'AuditPlan', id }],
    }),

    createAuditPlan: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/plans/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AuditPlan'],
    }),

    updateAuditPlan: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/plans/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      // AUD-33: getAuditPlans.providesTags is the bare ['AuditPlan'] tag
      // (not per-item id tags), so an invalidatesTags of only
      // {type:'AuditPlan', id} never matched it -- the plans list/register
      // never refetched after an edit, only the single-plan detail query
      // did. Also invalidate the bare 'AuditPlan' tag so the list refreshes.
      invalidatesTags: (result, error, { id }) => [{ type: 'AuditPlan', id }, 'AuditPlan'],
    }),

    deleteAuditPlan: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/audits/plans/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: ['AuditPlan'],
    }),

    // =========================================================================
    // Engagements
    // =========================================================================
    getEngagements: builder.query({
      // AUD-31: EngagementViewSet.ordering_fields is
      // ['planned_start_date', 'status', 'title'] -- 'created_at' isn't
      // one of them, so DRF's OrderingFilter silently dropped this default
      // and engagements came back in DB insertion order regardless of
      // what callers asked for.
      query: ({ filters = {}, page = 1, limit = 20, ordering = '-planned_start_date' } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());
        params.set('ordering', ordering);

        if (filters.status && filters.status !== 'all') {
          params.set('status', filters.status);
        }
        if (filters.type) {
          // AUD-31: filterset_fields has 'engagement_type', not 'type' --
          // this param was silently ignored by DjangoFilterBackend.
          params.set('engagement_type', filters.type);
        }
        if (filters.search) {
          params.set('search', filters.search);
        }
        // R12: EngagementViewSet.filterset_fields has always included
        // 'plan' server-side -- this frontend query builder just never
        // wired the param up, so plan-detail.jsx had to fetch the
        // tenant's 100 most-recently-started engagements across every
        // plan and filter client-side, silently truncating/dropping
        // older plans' engagements once a tenant exceeded 100 total.
        if (filters.plan) {
          params.set('plan', filters.plan);
        }

        return {
          url: `/api/v1/audits/engagements/?${params.toString()}`,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Engagement', id })),
              { type: 'Engagement', id: 'LIST' },
            ]
          : [{ type: 'Engagement', id: 'LIST' }],
    }),

    getEngagementById: builder.query({
      query: (id) => ({
        url: `/api/v1/audits/engagements/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'Engagement', id }],
    }),

    createEngagement: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/engagements/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Engagement', id: 'LIST' }, 'AuditStats'],
    }),

    updateEngagement: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/engagements/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Engagement', id },
        { type: 'Engagement', id: 'LIST' },
      ],
    }),

    deleteEngagement: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/audits/engagements/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'Engagement', id: 'LIST' }],
    }),

    // Engagement lifecycle actions (EngagementViewSet, apps/audit/views.py) --
    // status is read-only on EngagementDetailSerializer (AUD-12); these are
    // the only sanctioned way to move an engagement between workflow states.
    startEngagementFieldwork: builder.mutation({
      query: (id) => ({
        url: `/api/v1/audits/engagements/${id}/start_fieldwork/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Engagement', id },
        { type: 'Engagement', id: 'LIST' },
      ],
    }),

    completeEngagementFieldwork: builder.mutation({
      query: (id) => ({
        url: `/api/v1/audits/engagements/${id}/complete_fieldwork/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Engagement', id },
        { type: 'Engagement', id: 'LIST' },
      ],
    }),

    issueEngagementReport: builder.mutation({
      query: ({ id, overall_rating = 'not_rated', executive_summary = '' }) => ({
        url: `/api/v1/audits/engagements/${id}/issue_report/`,
        method: 'POST',
        body: { overall_rating, executive_summary },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Engagement', id },
        { type: 'Engagement', id: 'LIST' },
        'AuditStats',
      ],
    }),

    // =========================================================================
    // Engagement Milestones & Dependencies (Audit Timeline Scheduler)
    // =========================================================================
    getEngagementMilestones: builder.query({
      query: (engagementId) => ({
        url: '/api/v1/audits/engagement-milestones/',
        params: engagementId ? { engagement: engagementId, page_size: 100 } : { page_size: 100 },
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['EngagementMilestone'],
    }),

    createEngagementMilestone: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/engagement-milestones/',
        method: 'POST',
        body: data,
      }),
      // AUD-14: bare 'Engagement' (not just LIST): the engagement detail
      // embeds milestones, so it must refetch too.
      invalidatesTags: ['EngagementMilestone', 'Engagement'],
    }),

    updateEngagementMilestone: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/engagement-milestones/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      // AUD-14: bare 'Engagement' (not just LIST): the engagement detail
      // embeds milestones, so it must refetch too.
      invalidatesTags: ['EngagementMilestone', 'Engagement'],
    }),

    getEngagementDependencies: builder.query({
      query: (engagementId) => ({
        url: '/api/v1/audits/engagement-dependencies/',
        params: engagementId ? { engagement: engagementId, page_size: 100 } : { page_size: 100 },
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['EngagementDependency'],
    }),

    createEngagementDependency: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/engagement-dependencies/',
        method: 'POST',
        body: data,
      }),
      // AUD-14: see createEngagementMilestone.
      invalidatesTags: ['EngagementDependency', 'Engagement'],
    }),

    // AUD-14: no delete hooks existed for milestones/dependencies.
    deleteEngagementMilestone: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/audits/engagement-milestones/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: ['EngagementMilestone', 'Engagement'],
    }),

    deleteEngagementDependency: builder.mutation({
      query: (id) => ({
        url: `/api/v1/audits/engagement-dependencies/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EngagementDependency', 'Engagement'],
    }),

    // AUD-14: engagement team (EngagementViewSet.team, GET/POST/DELETE).
    getEngagementTeam: builder.query({
      query: (engagementId) => ({ url: `/api/v1/audits/engagements/${engagementId}/team/` }),
      providesTags: (result, error, engagementId) => [{ type: 'EngagementTeam', id: engagementId }],
    }),

    addEngagementTeamMember: builder.mutation({
      query: ({ engagementId, ...body }) => ({
        url: `/api/v1/audits/engagements/${engagementId}/team/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { engagementId }) => [
        { type: 'EngagementTeam', id: engagementId },
        'Engagement',
      ],
    }),

    // The backend takes the user to remove in the DELETE body ({user_id}).
    removeEngagementTeamMember: builder.mutation({
      query: ({ engagementId, userId }) => ({
        url: `/api/v1/audits/engagements/${engagementId}/team/`,
        method: 'DELETE',
        body: { user_id: userId },
      }),
      invalidatesTags: (result, error, { engagementId }) => [
        { type: 'EngagementTeam', id: engagementId },
        'Engagement',
      ],
    }),

    // AUD-14: engagements where the caller is lead auditor or on the team.
    getMyEngagements: builder.query({
      query: () => ({ url: '/api/v1/audits/engagements/my_engagements/' }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: [{ type: 'Engagement', id: 'MINE' }],
    }),

    // =========================================================================
    // Procedures (AUD-16)
    // =========================================================================
    getProcedures: builder.query({
      query: (engagementId) => ({
        url: '/api/v1/audits/procedures/',
        params: listParams({ engagement: engagementId }),
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['Procedure'],
    }),

    createProcedure: builder.mutation({
      query: (data) => ({ url: '/api/v1/audits/procedures/', method: 'POST', body: data }),
      invalidatesTags: ['Procedure', 'Engagement'],
    }),

    updateProcedure: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/procedures/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Procedure', 'Engagement'],
    }),

    deleteProcedure: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/audits/procedures/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: ['Procedure', 'Engagement'],
    }),

    completeProcedure: builder.mutation({
      query: ({ id, result, conclusion = '' }) => ({
        url: `/api/v1/audits/procedures/${id}/complete/`,
        method: 'POST',
        body: { result, conclusion },
      }),
      invalidatesTags: ['Procedure', 'Engagement'],
    }),

    reviewProcedure: builder.mutation({
      query: (id) => ({
        url: `/api/v1/audits/procedures/${id}/review/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Procedure', 'Engagement'],
    }),

    // =========================================================================
    // Evidence (AUD-26, immutable: list / upload / download only)
    // =========================================================================
    getEvidence: builder.query({
      query: ({ engagement, workpaper, procedure } = {}) => ({
        url: '/api/v1/audits/evidence/',
        params: listParams({ workpaper__engagement: engagement, workpaper, procedure }),
      }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['Evidence'],
    }),

    uploadEvidence: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/evidence/',
        method: 'POST',
        body: buildEvidenceFormData(data),
      }),
      invalidatesTags: ['Evidence', 'Workpaper'],
    }),

    downloadEvidence: builder.mutation({
      async queryFn({ id, filename }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `/api/v1/audits/evidence/${id}/download/`,
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'evidence';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),

    // =========================================================================
    // Templates (AUD-26)
    // =========================================================================
    getAuditTemplates: builder.query({
      query: (filters = {}) => ({ url: '/api/v1/audits/templates/', params: listParams(filters) }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['AuditTemplate'],
    }),

    createAuditTemplate: builder.mutation({
      query: (data) => ({ url: '/api/v1/audits/templates/', method: 'POST', body: data }),
      invalidatesTags: ['AuditTemplate'],
    }),

    updateAuditTemplate: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/templates/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['AuditTemplate'],
    }),

    applyAuditTemplate: builder.mutation({
      query: ({ id, engagementId }) => ({
        url: `/api/v1/audits/templates/${id}/apply_to_engagement/`,
        method: 'POST',
        body: { engagement_id: engagementId },
      }),
      invalidatesTags: ['Procedure', 'Engagement'],
    }),

    // =========================================================================
    // Audit universe (AUD-26, D14: the audit app's own endpoints)
    // =========================================================================
    getAuditUniverse: builder.query({
      query: (filters = {}) => ({ url: '/api/v1/audits/universe/', params: listParams(filters) }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['AuditUniverse'],
    }),

    getAuditUniverseTree: builder.query({
      query: () => ({ url: '/api/v1/audits/universe/tree/' }),
      providesTags: ['AuditUniverse'],
    }),

    getOverdueAuditUniverse: builder.query({
      query: () => ({ url: '/api/v1/audits/universe/overdue/' }),
      providesTags: ['AuditUniverse'],
    }),

    getHighRiskAuditUniverse: builder.query({
      query: () => ({ url: '/api/v1/audits/universe/high_risk/' }),
      providesTags: ['AuditUniverse'],
    }),

    createAuditUniverseEntity: builder.mutation({
      query: (data) => ({ url: '/api/v1/audits/universe/', method: 'POST', body: data }),
      invalidatesTags: ['AuditUniverse', 'AuditStats'],
    }),

    updateAuditUniverseEntity: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/universe/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['AuditUniverse'],
    }),

    deleteAuditUniverseEntity: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/audits/universe/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: ['AuditUniverse', 'AuditStats'],
    }),

    // =========================================================================
    // Workpapers
    // =========================================================================
    getWorkpapers: builder.query({
      // AUD-18: the default (no-engagement-selected) view calls this with
      // `undefined`. Unconditionally interpolating that into the query
      // string used to send `?engagement=undefined`, an invalid UUID that
      // WorkpaperViewSet's filterset_fields (django-filter) chokes on --
      // only include the param when an engagement id is actually given,
      // matching getEngagementMilestones/getEngagementDependencies above.
      query: (engagementId) => ({
        url: '/api/v1/audits/workpapers/',
        params: engagementId ? { engagement: engagementId } : undefined,
      }),
      transformResponse: (response) => response.results || response || [],
      providesTags: ['Workpaper'],
    }),

    createWorkpaper: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/workpapers/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Workpaper'],
    }),

    // AUD-17: workpaper review flow (draft -> pending_review -> reviewed ->
    // finalized; a return sends it back to draft with comments).
    // Approval routing C2 (Task 12): `id` or `{ id, approvers }` (picks keyed by flow step
    // order); submitting opens the workpaper's review approval.
    submitWorkpaperForReview: builder.mutation({
      query: (arg) => {
        const { id, approvers } = typeof arg === 'object' ? arg : { id: arg };
        return {
          url: `/api/v1/audits/workpapers/${encodeURIComponent(id)}/submit_for_review/`,
          method: 'POST',
          body: approvers ? { approvers } : {},
        };
      },
      invalidatesTags: ['Workpaper'],
    }),

    /** One workpaper's DETAIL record: approval_request_id/approval_status (R19). */
    getWorkpaperById: builder.query({
      query: (id) => ({ url: `/api/v1/audits/workpapers/${encodeURIComponent(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'Workpaper', id }],
    }),

    reviewWorkpaper: builder.mutation({
      query: ({ id, approve, review_notes = '' }) => ({
        url: `/api/v1/audits/workpapers/${id}/review/`,
        method: 'POST',
        body: { approve: approve === true, review_notes },
      }),
      invalidatesTags: ['Workpaper'],
    }),

    finalizeWorkpaper: builder.mutation({
      query: (id) => ({
        url: `/api/v1/audits/workpapers/${id}/finalize/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['Workpaper'],
    }),

    // =========================================================================
    // Findings
    // =========================================================================
    getFindings: builder.query({
      query: ({ filters = {}, page = 1, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());

        if (filters.finding_grade) {
          params.set('finding_grade', filters.finding_grade);
        }
        if (filters.finding_type) {
          params.set('finding_type', filters.finding_type);
        }
        if (filters.engagement) {
          params.set('engagement', filters.engagement);
        }
        if (filters.responsible_party) {
          params.set('responsible_party', filters.responsible_party);
        }
        if (filters.affected_unit) {
          params.set('affected_unit', filters.affected_unit);
        }
        if (filters.owning_unit) {
          params.set('owning_unit', filters.owning_unit);
        }
        if (filters.search) {
          params.set('search', filters.search);
        }

        return {
          url: `/api/v1/audits/findings/?${params.toString()}`,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: ['Finding'],
    }),

    // AUD-26: findings grouped by ISO 19011 grade.
    getFindingsByGrade: builder.query({
      query: () => ({ url: '/api/v1/audits/findings/by_grade/' }),
      providesTags: ['Finding'],
    }),

    getFindingGradeCounts: builder.query({
      query: () => ({ url: '/api/v1/audits/findings/grade_counts/' }),
      providesTags: ['Finding'],
    }),

    getFindingById: builder.query({
      query: (id) => ({
        url: `/api/v1/audits/findings/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'Finding', id }],
    }),

    createFinding: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/findings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Finding', 'AuditStats'],
    }),

    updateFinding: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/findings/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      // AUD-33: getFindings.providesTags is the bare ['Finding'] tag, same
      // issue as updateAuditPlan -- an id-only invalidate never matched it,
      // so the findings list/table never refetched after an edit.
      invalidatesTags: (result, error, { id }) => [{ type: 'Finding', id }, 'Finding'],
    }),

    // =========================================================================
    // Issues
    // =========================================================================
    getIssues: builder.query({
      query: ({ filters = {}, page = 1, limit = 20 } = {}) => {
        const params = new URLSearchParams();
        params.set('page', page.toString());
        params.set('page_size', limit.toString());

        if (filters.status) {
          params.set('status', filters.status);
        }
        if (filters.priority) {
          params.set('priority', filters.priority);
        }
        if (filters.owner) {
          params.set('owner', filters.owner);
        }
        if (filters.affected_unit) {
          params.set('affected_unit', filters.affected_unit);
        }
        if (filters.owning_unit) {
          params.set('owning_unit', filters.owning_unit);
        }
        if (filters.search) {
          params.set('search', filters.search);
        }

        return {
          url: `/api/v1/audits/issues/?${params.toString()}`,
        };
      },
      transformResponse: normalizeListResponse,
      providesTags: ['Issue'],
    }),

    getIssueById: builder.query({
      query: (id) => ({
        url: `/api/v1/audits/issues/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'Issue', id }],
    }),

    createIssue: builder.mutation({
      query: (data) => ({
        url: '/api/v1/audits/issues/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Issue', 'AuditStats'],
    }),

    // Issues have a required one-to-one link to a Finding, so they must be
    // created via the finding's `create_issue` action rather than the
    // standalone issues endpoint.
    createIssueFromFinding: builder.mutation({
      query: ({ findingId, ...data }) => ({
        url: `/api/v1/audits/findings/${findingId}/create_issue/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Issue', 'Finding', 'AuditStats'],
    }),

    updateIssue: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/audits/issues/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Issue', id }],
    }),

    // AUD-23: IssueViewSet's remediation lifecycle (start_remediation ->
    // submit_remediation -> verify -> close, plus extend_due_date and
    // comments) had no frontend caller at all, so issues created via the
    // findings/issues page could never progress past 'open'. These
    // endpoints give each backend action a caller.
    startIssueRemediation: builder.mutation({
      query: (id) => ({
        url: `/api/v1/audits/issues/${id}/start_remediation/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Issue', id }, 'Issue'],
    }),

    submitIssueRemediation: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/v1/audits/issues/${id}/submit_remediation/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Issue', id }, 'Issue'],
    }),

    verifyIssue: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/v1/audits/issues/${id}/verify/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Issue', id }, 'Issue'],
    }),

    closeIssue: builder.mutation({
      query: (id) => ({
        url: `/api/v1/audits/issues/${id}/close/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Issue', id }, 'Issue'],
    }),

    extendIssueDueDate: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/v1/audits/issues/${id}/extend_due_date/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Issue', id }, 'Issue'],
    }),

    getOverdueIssues: builder.query({
      query: () => ({ url: '/api/v1/audits/issues/overdue/' }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['Issue'],
    }),

    getMyIssues: builder.query({
      query: () => ({ url: '/api/v1/audits/issues/my_issues/' }),
      transformResponse: (response) => response?.results || response || [],
      providesTags: ['Issue'],
    }),

    createIssueComment: builder.mutation({
      query: ({ issue, ...body }) => ({
        url: '/api/v1/audits/issue-comments/',
        method: 'POST',
        body: { issue, ...body },
      }),
      invalidatesTags: (result, error, { issue }) => [{ type: 'Issue', id: issue }],
    }),

    // =========================================================================
    // Statistics
    // =========================================================================
    getAuditSummary: builder.query({
      query: () => ({
        url: '/api/v1/audits/dashboard/',
      }),
      providesTags: ['AuditStats'],
    }),

    // =========================================================================
    // Workflow Transitions (WorkflowEngine)
    // POST /api/v1/audits/plans/{id}/transition/
    // GET  /api/v1/audits/plans/{id}/transitions/available/
    // GET  /api/v1/audits/plans/{id}/transitions/history/
    // NEW AuditPlan states: planned / in_progress / reported / finding_open / closed
    // Paths must match apps/shared/workflow/mixins.py's WorkflowViewMixin
    // (url_path='transitions/available' / 'transitions/history') --
    // AUD-09 review round 1: these were previously available_transitions/ and
    // transition_history/, which 404 against the real backend.
    // =========================================================================

    /**
     * Transition an audit plan to a new workflow state.
     * Body: { to_state, comment?, evidence_ref? }
     * Replaces submitAuditPlan / approveAuditPlan / startAuditPlan
     */
    transitionAuditPlan: builder.mutation({
      query: ({ id, to_state, comment = '', evidence_ref = '' }) => ({
        url: `/api/v1/audits/plans/${id}/transition/`,
        method: 'POST',
        body: { to_state, comment, evidence_ref },
      }),
      // AUD-33: getAuditPlans provides the bare 'AuditPlan' tag, not a
      // {type:'AuditPlan', id:'LIST'} tag, so id:'LIST' here never matched
      // it and the register didn't refresh after a status transition.
      invalidatesTags: (result, error, { id }) => [
        { type: 'AuditPlan', id },
        'AuditPlan',
        { type: 'WorkflowTransition', id },
        { type: 'AuditStats' },
      ],
    }),

    /**
     * Get available transitions for an audit plan from its current state.
     */
    getAuditPlanAvailableTransitions: builder.query({
      query: (id) => ({ url: `/api/v1/audits/plans/${id}/transitions/available/` }),
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),

    /**
     * Get full transition history log for an audit plan.
     */
    getAuditPlanTransitionHistory: builder.query({
      query: (id) => ({ url: `/api/v1/audits/plans/${id}/transitions/history/` }),
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),
  }),
});

export const {
  // Audit Plans
  useGetAuditPlansQuery,
  useGetAuditPlanByIdQuery,
  useCreateAuditPlanMutation,
  useUpdateAuditPlanMutation,
  useDeleteAuditPlanMutation,
  // Engagements
  useGetEngagementsQuery,
  useGetEngagementByIdQuery,
  useCreateEngagementMutation,
  useUpdateEngagementMutation,
  useDeleteEngagementMutation,
  useStartEngagementFieldworkMutation,
  useCompleteEngagementFieldworkMutation,
  useIssueEngagementReportMutation,
  // Engagement Milestones & Dependencies
  useGetEngagementMilestonesQuery,
  useCreateEngagementMilestoneMutation,
  useUpdateEngagementMilestoneMutation,
  useGetEngagementDependenciesQuery,
  useCreateEngagementDependencyMutation,
  useDeleteEngagementMilestoneMutation,
  useDeleteEngagementDependencyMutation,
  // Team (AUD-14)
  useGetEngagementTeamQuery,
  useAddEngagementTeamMemberMutation,
  useRemoveEngagementTeamMemberMutation,
  useGetMyEngagementsQuery,
  // Procedures (AUD-16)
  useGetProceduresQuery,
  useCreateProcedureMutation,
  useUpdateProcedureMutation,
  useDeleteProcedureMutation,
  useCompleteProcedureMutation,
  useReviewProcedureMutation,
  // Evidence, templates, universe (AUD-26)
  useGetEvidenceQuery,
  useUploadEvidenceMutation,
  useDownloadEvidenceMutation,
  useGetAuditTemplatesQuery,
  useCreateAuditTemplateMutation,
  useUpdateAuditTemplateMutation,
  useApplyAuditTemplateMutation,
  useGetAuditUniverseQuery,
  useGetAuditUniverseTreeQuery,
  useGetOverdueAuditUniverseQuery,
  useGetHighRiskAuditUniverseQuery,
  useCreateAuditUniverseEntityMutation,
  useUpdateAuditUniverseEntityMutation,
  useDeleteAuditUniverseEntityMutation,
  // Workpapers
  useGetWorkpapersQuery,
  useCreateWorkpaperMutation,
  useSubmitWorkpaperForReviewMutation,
  useGetWorkpaperByIdQuery,
  useReviewWorkpaperMutation,
  useFinalizeWorkpaperMutation,
  // Findings
  useGetFindingsQuery,
  useGetFindingsByGradeQuery,
  useGetFindingGradeCountsQuery,
  useGetFindingByIdQuery,
  useCreateFindingMutation,
  useUpdateFindingMutation,
  // Issues
  useGetIssuesQuery,
  useGetIssueByIdQuery,
  useCreateIssueMutation,
  useCreateIssueFromFindingMutation,
  useUpdateIssueMutation,
  useStartIssueRemediationMutation,
  useSubmitIssueRemediationMutation,
  useVerifyIssueMutation,
  useCloseIssueMutation,
  useExtendIssueDueDateMutation,
  useGetOverdueIssuesQuery,
  useGetMyIssuesQuery,
  useCreateIssueCommentMutation,
  // Stats
  useGetAuditSummaryQuery,
  // Workflow
  useTransitionAuditPlanMutation,
  useGetAuditPlanAvailableTransitionsQuery,
  useGetAuditPlanTransitionHistoryQuery,
} = auditApi;

export default auditApi;
